import { capitalCase } from 'change-case';
import { cloneDeep, omit, pick, sum } from 'lodash';
import { getFramework } from '@/framework';
import { getTableEditState } from '@/plugins/table-edit/state';
import addOrUpdateLayer from '@/plugins/table-edit/utils/add-or-update-layer';
import { COL_ELEMENT_IS_BLOCK } from '@/utils/common-kit';
import { Border, Cell, CellStyle, TextAlignType } from '@/version-types';
import { emptyBorder, isEqualBorder } from '../utils/cell';
import {
    getActiveTableLayer,
    getCellStyle,
    getFixedHsizes,
    getUpdatedTableStyle,
    GetUpdatedTableStyle_Action,
} from '../utils/table';
import { cancelMergeCells, mergeCells } from './merge-cells';

export const getCellsActions = ({
    frameworkIdentity,
    mode,
}: {
    frameworkIdentity: string;
    mode: 'selectedCells' | 'allCells';
}) => {
    const framework = getFramework(frameworkIdentity);

    const getTargetCellIds = (mode: 'selectedCells' | 'allCells'): string[] => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return [];
        if (mode === 'allCells') {
            const cells = activeTableLayer.data.cells.flat().filter(Boolean) as Cell[];
            return cells.map((c) => c.__renderId);
        }
        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(frameworkIdentity);
        if (cellsSelectedInfo) {
            return cellsSelectedInfo.cellInfoList.map((c) => c.id);
        }
        if (rowOrColSelectedRectInfo) {
            const startHIndex = rowOrColSelectedRectInfo.direction === 'vertical' ? rowOrColSelectedRectInfo.index : 0;
            const startVIndex =
                rowOrColSelectedRectInfo.direction === 'horizontal' ? rowOrColSelectedRectInfo.index : 0;
            const endHIndex =
                rowOrColSelectedRectInfo.direction === 'vertical'
                    ? rowOrColSelectedRectInfo.index + 1
                    : activeTableLayer.data.baseCells.horizontal.length;
            const endVIndex =
                rowOrColSelectedRectInfo.direction === 'horizontal'
                    ? rowOrColSelectedRectInfo.index + 1
                    : activeTableLayer.data.baseCells.vertical.length;
            const targetCells = activeTableLayer.data.cells
                .slice(startVIndex, endVIndex)
                .map((row) => row.slice(startHIndex, endHIndex))
                .flat()
                .filter(Boolean) as Cell[];
            return targetCells.map((c) => c.__renderId);
        }
        return [];
    };

    type CellStyleParams = Partial<Omit<CellStyle, 'border'>>;
    type CellStyleParamsKey = keyof CellStyleParams;

    const setCellStyle = async (styleParams: CellStyleParams) => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        if (!targetCellIds.length) return;

        let newTableLayer = cloneDeep(activeTableLayer);
        const { cells: cellsProp, styles } = newTableLayer.data;
        const allCells = cellsProp.flat().filter(Boolean) as Cell[];
        const targetCells = allCells.filter((c) => targetCellIds.includes(c.__renderId));
        if (!targetCells.length) return;

        const setStyleItem = async <K extends CellStyleParamsKey>(type: K, value: CellStyleParams[K]) => {
            const newStyleId = (Math.max(...Object.keys(styles).map(Number)) + 1).toString();
            styles[newStyleId] = {
                [type]: value,
            };

            // 下划线和删除线互斥
            if (type === 'underline' && value) {
                styles[newStyleId].lineThrough = false;
            } else if (type === 'lineThrough' && value) {
                styles[newStyleId].underline = false;
            }

            targetCells.forEach((cell) => {
                // 去掉多余的引用, 防止数据越来越大
                cell.styleIds = cell.styleIds.filter((id) => !(type in newTableLayer.data.styles[id]));
                cell.styleIds.push(newStyleId);
            });
        };

        const paramsKeys = Object.keys(styleParams);
        const promises = paramsKeys.map((key) => setStyleItem(key as CellStyleParamsKey, styleParams[key]));
        await Promise.all(promises);

        const {
            hasEditingText,
            baseCellsBeforeEditing,
            activeCell: _activeCell,
        } = getTableEditState(frameworkIdentity);
        const activeCell = newTableLayer.data.cells.flat().find((c) => c?.__renderId === _activeCell?.id);

        const shouldUpdateTableLayoutStyleKeys = ['fontFamily', 'fontSize', 'disableAutoWrap'] as const;
        if (paramsKeys.some((key) => shouldUpdateTableLayoutStyleKeys.includes(key as any))) {
            if (styleParams.fontFamily) {
                // 需要先更新一次数据 (带上最新fontFamily), 否则layer-loading的plugin无法知道是哪些单元格需要loading
                newTableLayer = addOrUpdateLayer({
                    frameworkIdentity,
                    layer: newTableLayer,
                    resetMaterialIds: true,
                });
                framework.operate.setActiveLayer(newTableLayer.id);
                // 加载全量字体
                await framework.registerFont(newTableLayer, styleParams.fontFamily);
            }

            const overwriteStyle = pick(styleParams, shouldUpdateTableLayoutStyleKeys);

            let delta = 0;

            const newInfo = getUpdatedTableStyle({
                frameworkIdentity,
                tableLayerId: newTableLayer.id,
                tableRotate: newTableLayer.rotate,
                // 编辑中需要保留, 这样才能跟text-edit/adapter/table-layer.ts下的getUpdatedTableStyle的tempTableEl互通, 才能还原编辑前的布局
                preserveTempElement: hasEditingText,
                action: (_, { updateCellStyle, updateTextContentOrAttr }) => {
                    if (hasEditingText && activeCell) {
                        const shrinkToCellWidth = baseCellsBeforeEditing
                            ? sum(
                                  baseCellsBeforeEditing.horizontal.slice(
                                      activeCell.hIndex,
                                      activeCell.hIndex + activeCell.colSpan,
                                  ),
                              )
                            : undefined;
                        delta = updateTextContentOrAttr({
                            currentTableLayerData: newTableLayer.data,
                            fontFamilyMap: newTableLayer.__runtime?.fontFamilyMap ?? {},
                            cellInfo: {
                                id: activeCell.__renderId,
                                hIndex: activeCell.hIndex,
                                vIndex: activeCell.vIndex,
                            },
                            overwriteStyle,
                            shrinkToCellWidth,
                        }).delta;
                    } else {
                        updateCellStyle({
                            currentTableLayerData: activeTableLayer.data,
                            fontFamilyMap: activeTableLayer.__runtime?.fontFamilyMap ?? {},
                            cellInfoList: targetCells.map((c) => ({
                                id: c.__renderId,
                                hIndex: c.hIndex,
                                vIndex: c.vIndex,
                            })),
                            overwriteStyle,
                        });
                    }
                },
                compute: (_, { getLayout }) =>
                    getLayout({
                        tableWidth: activeTableLayer.width,
                        tableHeight: activeTableLayer.height,
                        horizontalSizes: activeTableLayer.data.baseCells.horizontal,
                        verticalSizes: activeTableLayer.data.baseCells.vertical,
                    }),
            });

            if (newInfo) {
                newTableLayer.height = newInfo.tableHeight;
                newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;

                if (COL_ELEMENT_IS_BLOCK) {
                    newTableLayer.width = newInfo.tableWidth;
                    newTableLayer.data.baseCells.horizontal = newInfo.baseCells.horizontal;
                } else if (hasEditingText && activeCell) {
                    newTableLayer.data.baseCells.horizontal = getFixedHsizes(newTableLayer);
                    newTableLayer.width += delta;
                    newTableLayer.data.baseCells.horizontal[activeCell.hIndex] += delta;
                }
            }
        }

        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: paramsKeys.includes('fontFamily'),
        });
        framework.operate.setActiveLayer(newTableLayer.id);
        framework.history.write(cloneDeep(activeTableLayer), newTableLayer);
    };

    type BizTextAlign = 'top' | 'vertical-center' | 'bottom' | 'left' | 'center' | 'right' | 'justify';
    interface BizCellStyleParams extends Omit<CellStyleParams, 'textAlign' | 'verticalAlign'> {
        align?: BizTextAlign;
    }
    type BizCellStyleParamsKey = keyof BizCellStyleParams;
    const setCellStyleByBiz = async (bizStyleParams: BizCellStyleParams) => {
        const styleParams: CellStyleParams = omit(bizStyleParams, ['align']);
        if (bizStyleParams.align) {
            if (['top', 'vertical-center', 'bottom'].includes(bizStyleParams.align)) {
                styleParams.verticalAlign = {
                    top: 'top',
                    'vertical-center': 'center',
                    bottom: 'bottom',
                }[bizStyleParams.align];
            } else {
                styleParams.textAlign = bizStyleParams.align as TextAlignType;
            }
        }
        return setCellStyle(styleParams);
    };

    const getSetCellStyleFn =
        <K extends BizCellStyleParamsKey>(type: K) =>
        (value: NonNullable<BizCellStyleParams[K]>) =>
            setCellStyleByBiz({ [type]: value });

    const setDisableAutoWrap = getSetCellStyleFn('disableAutoWrap');
    const setFontFamily = getSetCellStyleFn('fontFamily');
    const setFontSize = getSetCellStyleFn('fontSize');
    const setTextColor = getSetCellStyleFn('color');
    const setTextAttr = (mode: 'bold' | 'italic' | 'underline' | 'lineThrough', active: boolean) =>
        getSetCellStyleFn(mode)(active);
    const setBackgroundColor = getSetCellStyleFn('backgroundColor');
    const setTextAlign = getSetCellStyleFn('align');
    const setTextStyle = (textStyle: { fontFamily: string; fontSize: number; bold: boolean; italic: boolean }) =>
        setCellStyleByBiz(textStyle);

    const clearBorders = () => appendBorders('full', { ...emptyBorder });

    const appendBorders = (mode: 'wrap' | 'full' | 'top' | 'left' | 'right' | 'bottom', border: Border) => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;

        // 只支持表格整体的边框修改, 不支持某些单元格的边框修改
        const targetCellIds = getTargetCellIds('allCells');

        let newTableLayer = cloneDeep(activeTableLayer);

        type CellStyleBorderProp = NonNullable<CellStyle['border']>;
        const borderStylePool: { border: CellStyleBorderProp; styleId: string }[] = [];
        Object.entries(newTableLayer.data.styles).forEach(([styleId, style]) => {
            const styleKeys = Object.keys(style);
            if (styleKeys.length === 1 && styleKeys[0] === 'border') {
                borderStylePool.push({
                    styleId,
                    border: style.border!,
                });
            }
        });
        /** 返回styleId */
        function appendNewBorderStyle(border: CellStyleBorderProp): string {
            const isEqualBorderProp = (a: CellStyleBorderProp, b: CellStyleBorderProp) =>
                isEqualBorder(a.top, b.top) &&
                isEqualBorder(a.right, b.right) &&
                isEqualBorder(a.bottom, b.bottom) &&
                isEqualBorder(a.left, b.left);
            const poolItem = borderStylePool.find((item) => isEqualBorderProp(item.border, border));
            if (poolItem) return poolItem.styleId;

            const newStyleId = (Math.max(...Object.keys(styles).map(Number)) + 1).toString();
            styles[newStyleId] = { border };
            borderStylePool.push({ border, styleId: newStyleId });
            return newStyleId;
        }

        const { cells: cellsProp, styles } = newTableLayer.data;
        const cells = cellsProp.flat().filter(Boolean) as Cell[];
        const targetCells = cells.flat().filter((cell) => targetCellIds.includes(cell.__renderId));
        const sortedTargetCells = targetCells.sort((a, b) => (a.hIndex <= b.hIndex && a.vIndex <= b.vIndex ? -1 : 1));
        const lastCell = sortedTargetCells[sortedTargetCells.length - 1];
        const startHIndex = sortedTargetCells[0].hIndex;
        const startVIndex = sortedTargetCells[0].vIndex;
        const endHIndex = lastCell.hIndex + lastCell.colSpan;
        const endVIndex = lastCell.vIndex + lastCell.rowSpan;

        const tempTableElActions: GetUpdatedTableStyle_Action[] = [];

        const appendOneSideBorder = (dir: 'top' | 'left' | 'bottom' | 'right') => {
            let sideCells: Cell[];
            switch (dir) {
                case 'top':
                    sideCells = targetCells.filter((c) => c && c.vIndex === startVIndex);
                    break;
                case 'left':
                    sideCells = targetCells.filter((c) => c && c.hIndex === startHIndex);
                    break;
                case 'bottom':
                    sideCells = targetCells.filter((c) => c && c.vIndex + c.rowSpan === endVIndex);
                    break;
                case 'right':
                    sideCells = targetCells.filter((c) => c && c.hIndex + c.colSpan === endHIndex);
                    break;
            }
            sideCells.forEach((cell) => {
                const style = getCellStyle(cell, newTableLayer.data);
                const newBorder: CellStyleBorderProp = {
                    top: { ...emptyBorder },
                    right: { ...emptyBorder },
                    bottom: { ...emptyBorder },
                    left: { ...emptyBorder },
                    ...style.border,
                    [dir]: border,
                };
                const newStyleId = appendNewBorderStyle(newBorder);
                // 去掉多余的引用, 防止数据越来越大
                cell.styleIds = cell.styleIds.filter((id) => !('border' in newTableLayer.data.styles[id]));
                cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
            });

            const tempTableElAction: GetUpdatedTableStyle_Action = (_, { getTdEl }) => {
                sideCells.forEach((cell) => {
                    const tdEl = getTdEl(cell.__renderId);
                    if (!tdEl) return;
                    tdEl.style[`border${capitalCase(dir)}Width`] = `${border.width}px`;
                    tdEl.clientWidth;
                });
            };
            tempTableElActions.push(tempTableElAction);
        };

        switch (mode) {
            case 'wrap':
                appendOneSideBorder('top');
                appendOneSideBorder('left');
                appendOneSideBorder('bottom');
                appendOneSideBorder('right');
                break;
            case 'top':
            case 'left':
            case 'bottom':
            case 'right':
                appendOneSideBorder(mode);
                break;
            case 'full':
                {
                    const newStyleId = appendNewBorderStyle({
                        top: { ...border },
                        right: { ...border },
                        bottom: { ...border },
                        left: { ...border },
                    });
                    targetCells.forEach((cell) => {
                        // 去掉多余的引用, 防止数据越来越大
                        cell.styleIds = cell.styleIds.filter((id) => !('border' in newTableLayer.data.styles[id]));
                        if (cell.styleIds.includes(newStyleId)) {
                            cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                        } else {
                            cell.styleIds.push(newStyleId);
                        }
                    });

                    const tempTableElAction: GetUpdatedTableStyle_Action = (_, { getTdEl }) => {
                        targetCells.forEach((cell) => {
                            const tdEl = getTdEl(cell.__renderId);
                            if (!tdEl) return;
                            tdEl.style.borderWidth = `${border.width}px`;
                            tdEl.clientWidth;
                        });
                    };
                    tempTableElActions.push(tempTableElAction);
                }
                break;
        }

        const newInfo = getUpdatedTableStyle({
            frameworkIdentity,
            tableLayerId: newTableLayer.id,
            tableRotate: newTableLayer.rotate,
            action: (el, actionHelp) => {
                // 防止连续重排导致卡顿
                const oldDisplay = el.style.display;
                el.style.display = 'none';

                tempTableElActions.forEach((action) => action(el, actionHelp));

                el.style.display = oldDisplay;
            },
            compute: (_, { getLayout }) =>
                getLayout({
                    tableWidth: newTableLayer.width,
                    tableHeight: newTableLayer.height,
                    horizontalSizes: newTableLayer.data.baseCells.horizontal,
                    verticalSizes: newTableLayer.data.baseCells.vertical,
                }),
        });
        if (newInfo) {
            if (COL_ELEMENT_IS_BLOCK) {
                newTableLayer.width = newInfo.tableWidth;
                newTableLayer.height = newInfo.tableHeight;
                newTableLayer.data.baseCells = newInfo.baseCells;
            } else {
                // 降级处理
                const oldTableLayerData = activeTableLayer.data;
                const {
                    baseCells: { horizontal: oldHorizontalSizes },
                } = oldTableLayerData;

                let newBorderLeft = 0,
                    newBorderRight = 0;
                switch (mode) {
                    case 'full':
                    case 'wrap':
                        newBorderLeft = border.width;
                        newBorderRight = border.width;
                        break;
                    case 'left':
                        newBorderLeft = border.width;
                        break;
                    case 'right':
                        newBorderRight = border.width;
                        break;
                }

                newTableLayer.height = newInfo.tableHeight;
                newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
                newTableLayer.width = sum(oldHorizontalSizes) + newBorderLeft / 2 + newBorderRight / 2;
            }
        }
        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: false,
        });
        framework.operate.setActiveLayer(newTableLayer.id);
        framework.history.write(cloneDeep(activeTableLayer), newTableLayer);
    };

    const mergeThisCells = () => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        mergeCells({
            frameworkIdentity,
            tableLayer: activeTableLayer,
            targetCellIds,
        });
    };

    const cancelMergeThisCells = () => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        if (targetCellIds.length !== 1) return;
        cancelMergeCells({
            frameworkIdentity,
            tableLayer: activeTableLayer,
            targetCellId: targetCellIds[0],
        });
    };

    return {
        setDisableAutoWrap,
        setFontFamily,
        setFontSize,
        setTextColor,
        setTextAlign,
        setTextAttr,
        setTextStyle,
        setBackgroundColor,
        clearBorders,
        appendBorders,
        mergeCells: mergeThisCells,
        cancelMergeCells: cancelMergeThisCells,
    };
};

export type CellsActions = ReturnType<typeof getCellsActions>;
